import { ArrowLeft, TrendingUp as TrendIcon, Zap } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import { get } from "@/services/axiosUitils";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const ActiveMinutesDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;

  // API data states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeklyData, setWeeklyData] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState({
    active_zone_minutes: 0,
    minutes_today: 0,
    fat_burn_active_zone_minutes: 0,
    cardio_active_zone_minutes: 0,
    peak_active_zone_minutes: 0,
    fat_burn: 0,
    cardio: 0,
    peak: 0,
    min: 0,
    avg: 0,
    max: 0,
    days: 7,
    weeks: 4,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch active minutes data from API with selected date
  const fetchActiveMinutesData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/active-zone-minutes-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl);

      // Handle both boolean and object success formats
      const isSuccess =
        typeof response.success === "boolean"
          ? response.success
          : response.success?.code === "Retrieved";

      if (isSuccess && response.summary) {
        // Merge API response with current state to preserve zones data
        setSummaryData(prevData => ({
          ...prevData,
          ...response.summary,
          // Use API values if available, otherwise keep existing/default values
          fat_burn: response.summary?.fat_burn ?? prevData.fat_burn,
          cardio: response.summary?.cardio ?? prevData.cardio,
          peak: response.summary?.peak ?? prevData.peak,
        }));
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        if (type === "weekly") {
          // Transform weekly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            day: item.day || `Day ${item.value}`,
            minutes: item.value,
          }));
          setWeeklyData(chartData);
        } else {
          // Transform monthly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            week: item.week,
            minutes: item.value,
          }));
          setMonthlyData(chartData);
        }
      }
    } catch (err: any) {
      setError("Failed to fetch active minutes data");
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change and fetch data
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "weekly") {
      fetchActiveMinutesData("weekly");
    } else if (tab === "monthly") {
      fetchActiveMinutesData("monthly");
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchActiveMinutesData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }

    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${startDate} - ${endDate}`;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title3 font-bold text-foreground">
              Active Minutes
            </h1>
            {selectedDate ? (
              <p className="text-caption1 text-gray-500">
                {activeTab === "weekly" ? "Week of" : "Month of"}{" "}
                {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
              </p>
            ) : (
              <p className="text-caption1 text-muted-foreground">
                Your daily activity time
              </p>
            )}
          </div>
          <div className="w-10" />
        </div>

        {/* Today's Summary */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-green-50 to-green-100 p-5 shadow-lg dark:from-green-950 dark:to-green-900">
          <div className="mb-4 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-500/20">
              <Zap className="h-7 w-7 text-green-500" />
            </div>
            <div className="flex-1">
              <div className="text-large-title font-bold text-foreground">
                {loading
                  ? "..."
                  : (summaryData?.minutes_today || 0).toLocaleString()}
              </div>
              <p className="text-footnote text-muted-foreground">
                minutes {activeTab === "weekly" ? "this week" : "this month"}
              </p>
              {dateRange.start_date && (
                <p className="mt-1 text-caption1 text-gray-500">
                  {formatDateRange()}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 border-t border-green-200/50 pt-3">
            <div className="text-center">
              <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-yellow-500" />
              <div className="text-title3 font-bold text-foreground">
                {loading
                  ? "..."
                  : summaryData?.fat_burn_active_zone_minutes ||
                  summaryData?.fat_burn ||
                  0}
              </div>
              <div className="text-caption1 text-muted-foreground">Fat Burn</div>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-orange-500" />
              <div className="text-title3 font-bold text-foreground">
                {loading
                  ? "..."
                  : summaryData?.cardio_active_zone_minutes ||
                  summaryData?.cardio ||
                  0}
              </div>
              <div className="text-caption1 text-muted-foreground">Cardio</div>
            </div>
            <div className="text-center">
              <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-red-500" />
              <div className="text-title3 font-bold text-foreground">
                {loading
                  ? "..."
                  : summaryData?.peak_active_zone_minutes ||
                  summaryData?.peak ||
                  0}
              </div>
              <div className="text-caption1 text-muted-foreground">Peak</div>
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="rounded-2xl border border-border bg-green-50/30 p-4 shadow-lg dark:bg-green-950/30">
          <div className="flex gap-3">
            <TrendIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
            <div>
              <h4 className="mb-1 text-headline font-semibold text-foreground">
                About Active Minutes
              </h4>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Active minutes track time spent in moderate to vigorous physical
                activity. Fitbit detects this through elevated heart rate and
                movement intensity, helping you meet WHO's recommended 150
                minutes per week.
              </p>
            </div>
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 ${activeTab === "weekly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-subhead font-medium transition-all duration-200 ${activeTab === "monthly"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-background/50"
                  }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="space-y-4 px-4 pb-4 pt-0">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-green-500 border-t-transparent" />
                  <p className="text-footnote text-muted-foreground">
                    Loading active minutes data...
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="mb-2 text-footnote text-red-600">{error}</p>
                  <button
                    onClick={() =>
                      fetchActiveMinutesData(activeTab as "weekly" | "monthly")
                    }
                    className="text-footnote text-green-500 hover:text-green-600"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === "weekly" && (
              <div>
                <h3 className="mb-4 font-semibold text-foreground">
                  This Week's Active Minutes
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div
                  className="h-64 overflow-hidden outline-none"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={weeklyData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis
                        dataKey="day"
                        stroke="hsl(var(--muted-foreground))"
                        tick={CHART_AXIS_TICK_LG}
                        tickLine={false}
                        axisLine={false}
                        dy={10}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        tick={CHART_AXIS_TICK_LG}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted)/0.4)' }}
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                          color: "hsl(var(--foreground))",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                        formatter={(value: any) => [`${value} min`, "Active Time"]}
                      />
                      <Bar
                        dataKey="minutes"
                        fill="#22c55e"
                        radius={[6, 6, 0, 0]}
                        barSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === "monthly" && (
              <div>
                <h3 className="mb-4 font-semibold text-foreground">
                  This Month's Active Minutes
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                <div
                  className="h-64 overflow-hidden outline-none"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis
                        dataKey="week"
                        stroke="hsl(var(--muted-foreground))"
                        tick={CHART_AXIS_TICK_LG}
                        tickLine={false}
                        axisLine={false}
                        dy={10}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        tick={CHART_AXIS_TICK_LG}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: 'hsl(var(--muted)/0.4)' }}
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "2px solid hsl(var(--border))",
                          borderRadius: "12px",
                          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                          color: "hsl(var(--foreground))",
                        }}
                        itemStyle={{ color: "hsl(var(--foreground))" }}
                        labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                        formatter={(value: any) => [`${value} min`, "Active Time"]}
                      />
                      <Bar
                        dataKey="minutes"
                        fill="#22c55e"
                        radius={[6, 6, 0, 0]}
                        barSize={32}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ActiveMinutesDetail;
